/**
 * Iter136.28 — Shared client-side cache for the dynamic PWA branding
 * (title / short_name / icon URLs / theme_color).
 *
 * One fetch per page load, shared across every consumer, with an
 * event-bus refresh hook so the AdminPanel's save/upload/reset can
 * broadcast updates to all live components without a page reload.
 *
 * Why a custom hook (and not a Context provider)?
 *   * The branding is read by leaf components in many trees
 *     (LandingPage header, LoginPage hero, CompressorPage header,
 *     AdminPanel preview). Wrapping the whole app in yet another
 *     provider would add boilerplate; a module-level cache + a
 *     `useSyncExternalStore`-style subscription is simpler and works
 *     identically.
 *   * SSR is N/A here (CRA SPA), so a top-level fetch in module
 *     init is safe.
 *
 * Public API
 * ----------
 *   useBranding()            → { title, short_name, description,
 *                                 theme_color, icons, version,
 *                                 has_overrides, updated_at, loading }
 *   refreshBranding(state?)  → invalidate cache + broadcast (called
 *                                 by the admin panel after writes).
 *   loadBranding()           → returns the in-memory cached state
 *                                 (used by App.js boot effect to
 *                                 set <title>, theme-color meta).
 */
import { useEffect, useState, useSyncExternalStore } from "react";

// Static fallbacks must mirror frontend/public/manifest.json so the very
// first render before the network fetch resolves still shows the right
// brand on slow connections.
const STATIC_DEFAULTS = {
  title: "Air Compressor Canada",
  short_name: "Air Compressor Canada",
  description: "Air Compressor Canada portal — fleet, quotes, warranty claims, and AI assistant.",
  theme_color: "#ed1b24",
  icons: {
    "any-192": "/icon-192.png",
    "any-512": "/icon-512.png",
    "maskable-192": "/icon-maskable-192.png",
    "maskable-512": "/icon-maskable-512.png",
  },
  version: "static",
  has_overrides: false,
  updated_at: null,
  loading: true,
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── module-level state + pub-sub ─────────────────────────────────
let _state = { ...STATIC_DEFAULTS };
let _inflight = null;
const _listeners = new Set();

function _emit() {
  for (const l of _listeners) {
    try { l(); } catch { /* ignore */ }
  }
}

async function _fetch() {
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const r = await fetch(`${API}/pwa/branding`);
      if (r.ok) {
        const data = await r.json();
        _state = { ...STATIC_DEFAULTS, ...data, loading: false };
      } else {
        _state = { ..._state, loading: false };
      }
    } catch {
      _state = { ..._state, loading: false };
    } finally {
      _inflight = null;
      _emit();
    }
    return _state;
  })();
  return _inflight;
}

// Kick off the first fetch when this module is first imported. Single-
// flight: subsequent imports reuse the in-flight promise.
_fetch();

/** Subscribe to in-memory branding state. */
function _subscribe(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/** Snapshot for useSyncExternalStore. Reference-stable per state. */
function _getSnapshot() {
  return _state;
}

/** React hook — returns the current branding state and re-renders on update. */
export function useBranding() {
  // useSyncExternalStore would be ideal but to avoid hydration warnings
  // on older React/CRA setups we use a useEffect+useState pair with the
  // same pub-sub.
  const [, force] = useState(0);
  useEffect(() => _subscribe(() => force((n) => n + 1)), []);
  return _state;
}

/** Force a re-fetch + broadcast. Called by AdminPanel after write ops. */
export async function refreshBranding(initialState) {
  if (initialState && typeof initialState === "object") {
    _state = { ...STATIC_DEFAULTS, ...initialState, loading: false };
    _emit();
    return _state;
  }
  _inflight = null; // invalidate any in-flight cache
  return _fetch();
}

/** Synchronous read of the current state — used by App.js boot effect. */
export function loadBranding() {
  return _state;
}
